import { Component, inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, debounceTime } from 'rxjs';
import { DisbursmentService } from 'src/app/shared/service/disbursment/disbursment.service';
import { ToastrService } from 'ngx-toastr';
import { CreateTariffServiceComponent } from '../../../dis-repository/pages/create-tariff-service/create-tariff-service.component';
import { TariffService } from 'src/app/shared/service/disbursment/tariff-services.service';
import { fieldKeyResolver, MaTariffService } from './tariff-service.model';
import { CreateServiceDataComponent } from '../../../dis-repository/pages/create-tariff-service/create-service-data/create-service-data.component';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';

@Component({
  selector: 'app-tariff-services',
  templateUrl: './tariff-services.component.html',
  styleUrls: ['./tariff-services.component.scss'],
})
export class TariffServicesComponent {
  constructor(
    private dialog: MatDialog,
    private tariffService: TariffService,
    private snackbar: ToastrService,
    private requestFormService: RequestFormService,
  ) {}
  private loader = inject(DisbursmentService);
  isLoading$ = this.loader.loading$;
  displayedColumns: string[] = [];
  dynamicColumns: string[] = [];
  serviceData: MaTariffService[] = [];
  selectedTabIndex: number = 0;
  selectedTabName: string = '';
  dataSource = new MatTableDataSource<any>([]);
  searchTerm: string = '';
  tableSearchTerm: string = '';
  tabs: string[] = [];
  originalTabs: string[] = [];
  selectedServiceData: string[] = [];
  fieldKeyResolver = fieldKeyResolver;
  private tabSearchSubject = new Subject<string>();
  private tableSearchSubject = new Subject<string>();

  onTabChange(index: number) {
    this.selectedTabIndex = index;
    this.selectedTabName = this.tabs[index];
    this.dynamicColumns = [];
    this.dataSource.data = [];

    const selectedService = this.serviceData.find(
      (service) =>
        service.service_name?.toLowerCase() ===
        this.selectedTabName.toLowerCase(),
    );
    if (selectedService?.fields?.length) {
      this.dynamicColumns = selectedService.fields
        .map((f) => f.field_name)
        .filter((name): name is string => typeof name === 'string');
    }
    // set  displayed columns
    this.displayedColumns = ['Sl.No', ...this.dynamicColumns, 'action'];
    this.getSelectedServiceData(this.selectedTabName);
  }

  ngOnInit() {
    this.getServiceDataFromDb();

    // Setup debounce for tab search
    this.tabSearchSubject.pipe(debounceTime(1000)).subscribe(() => {
      this.onSearch();
    });

    // Setup debounce for table data search
    this.tableSearchSubject.pipe(debounceTime(1000)).subscribe(() => {
      this.onSearchTableData();
    });
  }

  ngOnDestroy() {
    this.tabSearchSubject.complete();
    this.tableSearchSubject.complete();
  }

  inputTabSearch(): void {
    if (this.searchTerm.trim() === '') {
      this.onSearch();
    } else {
      this.tabSearchSubject.next(this.searchTerm);
    }
  }

  inputTableSearch(): void {
    if (this.tableSearchTerm.trim() === '') {
      this.onSearchTableData();
    } else {
      this.tableSearchSubject.next(this.tableSearchTerm);
    }
  }

  //method to handle the search functionality for the tabs
  onSearch() {
    const filterValue = this.searchTerm.trim().toLowerCase();
    this.tabs = this.originalTabs.filter((tab) =>
      tab.toLowerCase().includes(filterValue),
    );

    if (this.tabs.length > 0) {
      this.selectedTabIndex = 0;
      this.onTabChange(0);
    } else {
      this.displayedColumns = [];
      this.dataSource.data = [];
    }
  }

  //method to handle the search functionality for the table data
  onSearchTableData() {
    const filteredValue = this.tableSearchTerm.trim().toLowerCase();

    // Reset to original data if search is empty
    if (filteredValue === '') {
      this.dataSource.data = this.selectedServiceData;
      this.dataSource.filter = '';
    } else {
      // Ensure we're filtering from the original data
      this.dataSource.data = this.selectedServiceData;
      this.dataSource.filter = filteredValue;
    }
  }

  //method to clear the searchTerm and reset the filter for the table data
  clearSearch() {
    this.tableSearchTerm = '';
    this.dataSource.filter = '';
    this.dataSource.data = this.selectedServiceData;
  }

  //method to clear the searchTerm and reset the filter for the tabs
  clearTabSearch() {
    this.searchTerm = '';
    this.tabs = [...this.originalTabs];
    this.selectedTabIndex = 0;
    this.onTabChange(0);
  }
  // method to open the Add Service dialog
  openAddServiceDialog(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.position = { right: '0' };
    dialogConfig.height = '100%';
    dialogConfig.width = '90vw';
    dialogConfig.maxWidth = '90vw';
    dialogConfig.panelClass = ['animate__animated', 'animate__slideInRight'];
    dialogConfig.disableClose = true;
    const dialogRef = this.dialog.open(
      CreateTariffServiceComponent,
      dialogConfig,
    );
    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'refresh') {
        this.getServiceDataFromDb();
      }
    });
  }

  openAddTabDialog(rowData: any): void {
    const selectedService = this.serviceData.find(
      (service) =>
        service.service_name?.toLowerCase() ===
        this.selectedTabName.toLowerCase(),
    );
    const dialogConfig = new MatDialogConfig();
    dialogConfig.position = { right: '0' };
    dialogConfig.height = '100%';
    dialogConfig.width = '85vw';
    dialogConfig.maxWidth = '90vw';
    dialogConfig.panelClass = ['animate__animated', 'animate__slideInRight'];
    dialogConfig.disableClose = true;
    const data: any = {
      serviceName: this.selectedTabName,
      fields: selectedService?.fields || [],
    };
    // Conditionally add rowData
    if (rowData != null) {
      data.rowData = rowData;
    }
    dialogConfig.data = data;
    const dialogRef = this.dialog.open(
      CreateServiceDataComponent,
      dialogConfig,
    );
    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'refresh') {
        this.getSelectedServiceData(this.selectedTabName);
      }
    });
  }

  // methos to get all the services from db
  getServiceDataFromDb() {
    this.loader.show();
    this.tariffService.serviceDataFromDb().subscribe({
      next: (response: any[]) => {
        this.tabs = response.map((service) => service.service_name);
        this.originalTabs = [...this.tabs];
        this.serviceData = response;
        this.loader.hide();
        this.onTabChange(0);
      },
      error: (err) => {
        const errMsg =
          err?.error?.error || 'Something went wrong. Please try again later.';
        this.snackbar.error(errMsg);
        this.loader.hide();
      },
    });
  }

  // method to get the selected servicedata
  getSelectedServiceData(serviceName: string) {
    this.loader.show();
    const formatedService = serviceName.replace(/\s+/g, '_').toLowerCase();
    this.tariffService.serviceDataByServiceName(formatedService).subscribe({
      next: (response: any) => {
        this.selectedServiceData = response.data;
        this.dataSource.data = this.selectedServiceData;
        this.loader.hide();
      },
      error: (err) => {
        const errMsg =
          err?.error?.error || 'Something went wrong. Please try again later.';
        this.snackbar.error(errMsg);
        this.loader.hide();
      },
    });
  }
  isDarkMode() {
    return this.requestFormService.isDarkMode();
  }
}
